package com.alpha.showcase.common.ai

import androidx.room3.InvalidationTracker
import androidx.room3.RoomOpenDelegate
import androidx.room3.migration.AutoMigrationSpec
import androidx.room3.migration.Migration
import androidx.room3.util.TableInfo
import androidx.room3.util.TableInfo.Companion.read
import androidx.room3.util.dropFtsSyncTriggers
import androidx.room3.util.performClear
import androidx.sqlite.SQLiteConnection
import androidx.sqlite.execSQL
import kotlin.Lazy
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlin.collections.Map
import kotlin.collections.MutableList
import kotlin.collections.MutableMap
import kotlin.collections.MutableSet
import kotlin.collections.Set
import kotlin.collections.mutableListOf
import kotlin.collections.mutableMapOf
import kotlin.collections.mutableSetOf
import kotlin.reflect.KClass

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class ImageSummaryDatabase_Impl : ImageSummaryDatabase() {
  private val _imageSummaryDao: Lazy<ImageSummaryDao> = lazy {
    ImageSummaryDao_Impl(this)
  }

  protected override fun createOpenDelegate(): RoomOpenDelegate {
    val _openDelegate: RoomOpenDelegate = object : RoomOpenDelegate(1, "24c913ef5b3a54f2fe39c3017d581a41", "5160e5742e9996dff74a4b54e228ccf0") {
      public override suspend fun createAllTables(connection: SQLiteConnection) {
        connection.execSQL("CREATE TABLE IF NOT EXISTS `summary_content` (`contentId` TEXT NOT NULL, `byteCount` INTEGER, PRIMARY KEY(`contentId`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `summary_revision` (`revisionId` TEXT NOT NULL, `contentId` TEXT NOT NULL, `body` TEXT NOT NULL, PRIMARY KEY(`revisionId`))")
        connection.execSQL("CREATE INDEX IF NOT EXISTS `index_summary_revision_contentId` ON `summary_revision` (`contentId`)")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `summary_head` (`contentId` TEXT NOT NULL, `revisionId` TEXT NOT NULL, PRIMARY KEY(`contentId`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS `summary_file_reference` (`contentId` TEXT NOT NULL, `referenceId` TEXT NOT NULL, `body` TEXT NOT NULL, PRIMARY KEY(`contentId`, `referenceId`))")
        connection.execSQL("CREATE TABLE IF NOT EXISTS room_master_table (id INTEGER PRIMARY KEY,identity_hash TEXT)")
        connection.execSQL("INSERT OR REPLACE INTO room_master_table (id,identity_hash) VALUES(42, '24c913ef5b3a54f2fe39c3017d581a41')")
      }

      public override suspend fun dropAllTables(connection: SQLiteConnection) {
        connection.execSQL("DROP TABLE IF EXISTS `summary_content`")
        connection.execSQL("DROP TABLE IF EXISTS `summary_revision`")
        connection.execSQL("DROP TABLE IF EXISTS `summary_head`")
        connection.execSQL("DROP TABLE IF EXISTS `summary_file_reference`")
      }

      public override suspend fun onCreate(connection: SQLiteConnection) {
      }

      public override suspend fun onOpen(connection: SQLiteConnection) {
        internalInitInvalidationTracker(connection)
      }

      public override suspend fun onPreMigrate(connection: SQLiteConnection) {
        dropFtsSyncTriggers(connection)
      }

      public override suspend fun onPostMigrate(connection: SQLiteConnection) {
      }

      public override suspend fun onValidateSchema(connection: SQLiteConnection): RoomOpenDelegate.ValidationResult {
        val _columnsSummaryContent: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsSummaryContent.put("contentId", TableInfo.Column("contentId", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsSummaryContent.put("byteCount", TableInfo.Column("byteCount", "INTEGER", false, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysSummaryContent: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesSummaryContent: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoSummaryContent: TableInfo = TableInfo("summary_content", _columnsSummaryContent, _foreignKeysSummaryContent, _indicesSummaryContent)
        val _existingSummaryContent: TableInfo = read(connection, "summary_content")
        if (!_infoSummaryContent.equals(_existingSummaryContent)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |summary_content(com.alpha.showcase.common.ai.SummaryContentRow).
              | Expected:
              |""".trimMargin() + _infoSummaryContent + """
              |
              | Found:
              |""".trimMargin() + _existingSummaryContent)
        }
        val _columnsSummaryRevision: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsSummaryRevision.put("revisionId", TableInfo.Column("revisionId", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsSummaryRevision.put("contentId", TableInfo.Column("contentId", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsSummaryRevision.put("body", TableInfo.Column("body", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysSummaryRevision: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesSummaryRevision: MutableSet<TableInfo.Index> = mutableSetOf()
        _indicesSummaryRevision.add(TableInfo.Index("index_summary_revision_contentId", false, listOf("contentId"), listOf("ASC")))
        val _infoSummaryRevision: TableInfo = TableInfo("summary_revision", _columnsSummaryRevision, _foreignKeysSummaryRevision, _indicesSummaryRevision)
        val _existingSummaryRevision: TableInfo = read(connection, "summary_revision")
        if (!_infoSummaryRevision.equals(_existingSummaryRevision)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |summary_revision(com.alpha.showcase.common.ai.SummaryRevisionRow).
              | Expected:
              |""".trimMargin() + _infoSummaryRevision + """
              |
              | Found:
              |""".trimMargin() + _existingSummaryRevision)
        }
        val _columnsSummaryHead: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsSummaryHead.put("contentId", TableInfo.Column("contentId", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsSummaryHead.put("revisionId", TableInfo.Column("revisionId", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysSummaryHead: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesSummaryHead: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoSummaryHead: TableInfo = TableInfo("summary_head", _columnsSummaryHead, _foreignKeysSummaryHead, _indicesSummaryHead)
        val _existingSummaryHead: TableInfo = read(connection, "summary_head")
        if (!_infoSummaryHead.equals(_existingSummaryHead)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |summary_head(com.alpha.showcase.common.ai.SummaryHeadRow).
              | Expected:
              |""".trimMargin() + _infoSummaryHead + """
              |
              | Found:
              |""".trimMargin() + _existingSummaryHead)
        }
        val _columnsSummaryFileReference: MutableMap<String, TableInfo.Column> = mutableMapOf()
        _columnsSummaryFileReference.put("contentId", TableInfo.Column("contentId", "TEXT", true, 1, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsSummaryFileReference.put("referenceId", TableInfo.Column("referenceId", "TEXT", true, 2, null, TableInfo.CREATED_FROM_ENTITY))
        _columnsSummaryFileReference.put("body", TableInfo.Column("body", "TEXT", true, 0, null, TableInfo.CREATED_FROM_ENTITY))
        val _foreignKeysSummaryFileReference: MutableSet<TableInfo.ForeignKey> = mutableSetOf()
        val _indicesSummaryFileReference: MutableSet<TableInfo.Index> = mutableSetOf()
        val _infoSummaryFileReference: TableInfo = TableInfo("summary_file_reference", _columnsSummaryFileReference, _foreignKeysSummaryFileReference, _indicesSummaryFileReference)
        val _existingSummaryFileReference: TableInfo = read(connection, "summary_file_reference")
        if (!_infoSummaryFileReference.equals(_existingSummaryFileReference)) {
          return RoomOpenDelegate.ValidationResult(false, """
              |summary_file_reference(com.alpha.showcase.common.ai.SummaryReferenceRow).
              | Expected:
              |""".trimMargin() + _infoSummaryFileReference + """
              |
              | Found:
              |""".trimMargin() + _existingSummaryFileReference)
        }
        return RoomOpenDelegate.ValidationResult(true, null)
      }
    }
    return _openDelegate
  }

  protected override fun createInvalidationTracker(): InvalidationTracker {
    val _shadowTablesMap: MutableMap<String, String> = mutableMapOf()
    val _viewTables: MutableMap<String, Set<String>> = mutableMapOf()
    return InvalidationTracker(this, _shadowTablesMap, _viewTables, "summary_content", "summary_revision", "summary_head", "summary_file_reference")
  }

  public override suspend fun clearAllTables() {
    performClear(this, false, "summary_content", "summary_revision", "summary_head", "summary_file_reference")
  }

  protected override fun getRequiredColumnTypeConverterClasses(): Map<KClass<*>, List<KClass<*>>> {
    val _columnTypeConvertersMap: MutableMap<KClass<*>, List<KClass<*>>> = mutableMapOf()
    _columnTypeConvertersMap.put(ImageSummaryDao::class, ImageSummaryDao_Impl.getRequiredColumnConverters())
    return _columnTypeConvertersMap
  }

  protected override fun getRequiredDaoReturnTypeConverterClasses(): Map<KClass<*>, List<KClass<*>>> {
    val _daoReturnTypeConvertersMap: MutableMap<KClass<*>, List<KClass<*>>> = mutableMapOf()
    _daoReturnTypeConvertersMap.put(ImageSummaryDao::class, ImageSummaryDao_Impl.getRequiredDaoReturnTypeConverters())
    return _daoReturnTypeConvertersMap
  }

  public override fun getRequiredAutoMigrationSpecClasses(): Set<KClass<out AutoMigrationSpec>> {
    val _autoMigrationSpecsSet: MutableSet<KClass<out AutoMigrationSpec>> = mutableSetOf()
    return _autoMigrationSpecsSet
  }

  public override fun createAutoMigrations(autoMigrationSpecs: Map<KClass<out AutoMigrationSpec>, AutoMigrationSpec>): List<Migration> {
    val _autoMigrations: MutableList<Migration> = mutableListOf()
    return _autoMigrations
  }

  public override fun summaries(): ImageSummaryDao = _imageSummaryDao.value
}
